import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";
import { privateOfficeService } from "@/services/private-office-service";
import {
  PrivateOfficeApprovalStatus,
  SearchPrivateOffice,
  SearchPrivateOfficesParams,
} from "@/services/private-office-service/types";
import { spaceService } from "@/services/space-service";
import { useCallback, useState } from "react";

export function useSearchVision() {
  const [privateOffices, setPrivateOffices] =
    useState<SearchPrivateOffice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo } = useAuth();

  const handleSearchVision = useCallback(
    async (params: SearchPrivateOfficesParams) => {
      setIsLoading(true);
      try {
        const space =
          params.term && params.term.length > 3
            ? await spaceService.getSpaceByLocation(params.term)
            : null;

        const term = params.term || "searchType";
        const request = {
          demands: {
            ...params.demands,
            ...(space && {
              [term]: {
                ...params.demands?.[term],
                ...(space && {
                  latitude: space.lat.toString(),
                  longitude: space.lng.toString(),
                }),
              },
            }),
          },
        };

        Object.keys(request.demands).forEach((key) => {
          //vision can list just approved rooms
          request.demands[key]["approvalStatus"] =
            PrivateOfficeApprovalStatus.approved;

          //vision can list only active PRIVATE_OFFICE rooms (no legacy, no deleted)
          request.demands[key]["includeLegacy"] = false;
          request.demands[key]["includeDeleted"] = false;
        });

        const response = await privateOfficeService.searchPrivateOffices({
          demands: request.demands,
        });
        setPrivateOffices(response);

        const demandKey =
          params.term || Object.keys(params.demands || {})[0] || "searchType";
        const filterValues = params.demands?.[demandKey] || {};

        let searchCityName = undefined;
        if (params.term) {
          const locationMatch = params.term.match(/,\s*([^,]+)$/);
          if (locationMatch) {
            searchCityName = locationMatch[1].trim();
          }
        }

        const uniqueCities = [
          ...new Set(
            response.results
              ?.map((r: any) => r.space_details?.cityname)
              .filter(Boolean),
          ),
        ] as string[];
        const uniqueNeighborhoods = [
          ...new Set(
            response.results
              ?.map((r: any) => r.space_details?.neighborhood)
              .filter(Boolean),
          ),
        ] as string[];

        const capacities =
          response.results?.map((r: any) => r.capacity).filter(Boolean) || [];
        const minCapacityReturned =
          capacities.length > 0 ? Math.min(...capacities) : undefined;
        const maxCapacityReturned =
          capacities.length > 0 ? Math.max(...capacities) : undefined;

        const prices =
          response.results?.map((r: any) => r.monthly_price).filter(Boolean) ||
          [];
        const minPriceReturned =
          prices.length > 0 ? Math.min(...prices) : undefined;
        const maxPriceReturned =
          prices.length > 0 ? Math.max(...prices) : undefined;

        sendTrackDataServer({
          eventName: "VISION_SEARCH_RESULTS_RENDERED",
          data: {
            deal_id: params?.dealUrl ?? null,
            user_id: userInfo.user?.id?.toString() ?? undefined,
            email: userInfo.profile?.email ?? undefined,
            has_results: response.private_rooms_returned_ids?.length > 0,
            results_count: response.total_results,
            demands: response.demands,
            spaces_returned: response.spaces_returned,
            spaces_returned_ids: response.spaces_returned_ids,
            private_rooms_returned_ids: response.private_rooms_returned_ids,
            demands_without_results: response.demands_without_results,
            cities_returned: uniqueCities,
            neighborhoods_returned: uniqueNeighborhoods,
            min_capacity_returned: minCapacityReturned,
            max_capacity_returned: maxCapacityReturned,
            min_price_returned: minPriceReturned,
            max_price_returned: maxPriceReturned,
            search_term: params.term || "searchType",
            search_city_name: searchCityName,
            search_latitude: filterValues.latitude,
            search_longitude: filterValues.longitude,
            filter_capacity_min: filterValues.minCapacity,
            filter_capacity_max: filterValues.maxCapacity,
            filter_capacity_weight:
              filterValues.minCapacityWeight ?? filterValues.maxCapacityWeight,
            filter_price_min: filterValues.minPrice,
            filter_price_max: filterValues.maxPrice,
            filter_price_weight:
              filterValues.minPriceWeight ?? filterValues.maxPriceWeight,
            filter_unit_credit_price: filterValues.unitCreditPrice,
            filter_distance: filterValues.distance,
            filter_amenities: filterValues.amenitiesSlugs,
            filter_amenities_optional: filterValues.amenitiesSlugsOptional,
            filter_status: filterValues.privateOfficeStatus,
            filter_skip_recent_updates: filterValues.skipRecentUpdatesFilter,
          },
          url: location.pathname,
          productType: "PO",
        });
      } catch (error) {
        console.error(error);
        setPrivateOffices(null);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    privateOffices,
    handleSearchVision,
    isLoading,
    setIsLoading,
    setPrivateOffices,
  };
}
